import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { PATHS } from '../../../constants/routes';
import { RootState } from '../../../store/store';
import ActionLinkButton from '../../UI/ActionLinkButton/ActionLinkButton';
import Menu from './menu/Menu';
import Search from './search/Search';
import { useIsScrolled } from '../../../hooks/useScrollInfo';
import SidePopup from '../SidePopup/SidePopup';
import AuthPopupContent from './authPopup/AuthPopupContent';
import './header.scss';

type HeaderProps = {
    isMobile?: boolean;
};

const Header: React.FC<HeaderProps> = ({ isMobile }) => {
    const location = useLocation();
    const scrolled = useIsScrolled(0);
    const { wishlist, cart } = useSelector((state: RootState) => state.user);
    const { categories } = useSelector((state: RootState) => state.category);
    const { products } = useSelector((state: RootState) => state.product);
    const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);
    const [authPopupOpen, setAuthPopupOpen] = useState(false);
    const totalProductsQuantityInCart = useMemo(() => (cart || []).reduce((res, val) => res + (Number(val?.quantity) || 0), 0), [cart]);
    const wishlistCount = useMemo(() => products.filter((product) => wishlist.includes(product.id)).length, [products, wishlist]);
    const userName = useMemo(() => {
        if (!isAuthenticated || !user) return null;
        const n = String(user.firstName ?? '').trim();
        return n.length ? n : null;
    }, [isAuthenticated, user]);

    const closeAuthPopup = useCallback(() => setAuthPopupOpen(false), []);
    const openAuthPopup = useCallback(() => setAuthPopupOpen(true), []);

    useEffect(() => {
        if (!authPopupOpen) return;
        closeAuthPopup();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    useEffect(() => {
        if (!authPopupOpen) return;
        if (isAuthenticated) closeAuthPopup();
    }, [authPopupOpen, isAuthenticated, closeAuthPopup]);

    const renderAccountButton = useCallback(
        (variant: 'desktop' | 'mobile') => {
            const iconSrc = variant === 'desktop' ? '/icons/new/user.svg' : '/icons/mobile/user.svg';
            const iconSrcDark = variant === 'desktop' ? '/icons/mobile/user_w.svg' : '/icons/mobile/user_w.svg';
            if (isAuthenticated) {
                return (
                    <ActionLinkButton
                        to={PATHS.account}
                        iconSrc={iconSrc}
                        iconSrcDark={iconSrcDark}
                        text={variant === 'desktop' ? (userName ?? undefined) : undefined}
                        ariaLabel="Личный кабинет"
                    />
                );
            }
            return <ActionLinkButton onClick={openAuthPopup} iconSrc={iconSrc} iconSrcDark={iconSrcDark} ariaLabel="Вход или регистрация" />;
        },
        [isAuthenticated, openAuthPopup, userName],
    );

    return (
        <>
            <header className={`header${scrolled ? ' header_scrolled' : ''}`}>
                {!isMobile ? (
                    <div className={['headerContent', scrolled && 'headerContent_scrolled'].filter(Boolean).join(' ')}>
                        <div className="headerContent__wrapper headerContent__wrapper_start">
                            <Menu scrolled={scrolled} categories={categories} isMobile={isMobile} isAuthenticated={isAuthenticated} openAuthPopup={openAuthPopup} />
                            <Search scrolled={scrolled} isMobile={isMobile} />
                        </div>
                        <Link className="headerContent__logo" to="/" aria-label="На главную">
                            <img
                                className={`headerContent__logo_img ${scrolled ? 'headerContent__logo_img_filter' : ''}`}
                                src="/logo.png"
                                alt="С. Габай — логотип"
                            />
                        </Link>
                        <div className="headerContent__wrapper">
                            <a
                                className="headerContent__phoneWrapper_value headerContent__phoneWrapper_value_hidden"
                                aria-labelledby="support-label"
                                href="tel:+74952806055"
                            >
                                +7 (495) 280-60-55
                            </a>
                            {wishlistCount > 0 ? (
                                <ActionLinkButton
                                    to={PATHS.wishlist}
                                    iconSrc="/icons/new/like_disactive_black.svg"
                                    iconSrcDark="/icons/mobile/shop_w.svg"
                                    count={wishlistCount}
                                />
                            ) : null}
                            <ActionLinkButton
                                to={PATHS.cart}
                                iconSrc="/icons/new/cart.svg"
                                iconSrcDark="/icons/mobile/shop_w.svg"
                                count={totalProductsQuantityInCart}
                            />
                            {/* <ActionLinkButton to={PATHS.account} iconSrc="/icons/new/user.svg" iconSrcDark="/icons/mobile/user_w.svg" text={userName} /> */}
                            {renderAccountButton('desktop')}
                        </div>
                    </div>
                ) : (
                    <div className={['headerContent', scrolled && 'headerContent_scrolled'].filter(Boolean).join(' ')}>
                        <Link className="headerContent__logo" to="/" aria-label="На главную">
                            <img
                                className={`headerContent__logo_img ${scrolled ? 'headerContent__logo_img_filter' : ''}`}
                                src="/logo.png"
                                alt="С. Габай — логотип"
                            />
                        </Link>
                        <div className="headerContent__wrapper">
                            <div className="headerContent__wrapper_hotBtns">
                                {wishlistCount > 0 ? (
                                    <ActionLinkButton
                                        to={PATHS.wishlist}
                                        iconSrc="/icons/mobile/heart.svg"
                                        iconSrcDark="/icons/mobile/shop_w.svg"
                                        count={wishlistCount}
                                    />
                                ) : null}
                                {renderAccountButton('mobile')}
                                <Search scrolled={scrolled} isMobile={isMobile} />
                                <ActionLinkButton
                                    to={PATHS.cart}
                                    iconSrc="/icons/mobile/shop.svg"
                                    iconSrcDark="/icons/mobile/shop_w.svg"
                                    count={totalProductsQuantityInCart}
                                />
                                <Menu
                                    scrolled={scrolled}
                                    categories={categories}
                                    isMobile={isMobile}
                                    isAuthenticated={isAuthenticated}
                                    openAuthPopup={openAuthPopup}
                                />
                            </div>
                        </div>
                        {location.pathname === '/' && !scrolled && (
                            <div className="headerContent__phoneWrapper">
                                <a className="headerContent__phoneWrapper_value" aria-labelledby="support-label" href="tel:+74952806055">
                                    +7 (495) 280-60-55
                                </a>
                                <label id="support-label" className="headerContent__phoneWrapper_label">
                                    Служба поддержки клиентов
                                </label>
                            </div>
                        )}
                    </div>
                )}
            </header>
            {/* Попап авторизации/регистрации (справа) */}
            <SidePopup open={authPopupOpen} onClose={closeAuthPopup} className="headerAuthPopup" showCloseButton={false}>
                <AuthPopupContent onClose={closeAuthPopup} />
            </SidePopup>
        </>
    );
};

export default Header;
